import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { SettingsField, SettingsLbl, SettingsInput, SettingsHintSm } from './ispSettingsUI';
import {
   StepEyebrow,
   StepTitle,
   StepNote,
   ScriptRow,
   ScriptLabel,
   ScriptPre,
   FallbackLink,
   FallbackNote,
   CopyBtn,
   WaitRow,
   Pulse,
   Stepper,
   Bullet,
   Connector,
   StepCaption,
   StepPane,
   NavRow,
   NavBtns,
} from './ispWizardUI';
import {
   createIspServer,
   getIspServerModels,
   getIspServerScript,
   getIspServers,
   type IspScriptPart,
} from '@/api/isp';

/**
 * Add a MikroTik to ISP Business — the same wizard shape as adding a vendo
 * (Ray, 2026-08-28: "the UI here should be similar in adding a vendo").
 *
 * ─── ONE STEP AT A TIME, WITH BACK AND NEXT ───
 * Ray, 2026-08-29: *"i dont like compacting it in 1 screen. i want similar to
 * adding vendo, a previous and next button in each step."* The steps used to be
 * stacked on one screen, which made the operator scroll past finished work to
 * reach the thing they were on. Bullets across the top, one body, Back and Next.
 *
 * ─── 🔴 TWO PASTES, NOT ONE ───
 * Ray, 2026-08-29: *"that long script will truncated."* Winbox's terminal drops
 * the tail of a long paste and says NOTHING — the prompt comes back, the
 * operator believes it worked, and the MikroTik is half configured. The
 * Add-Vendo MikroTik flow already splits its paste into step 4a and 4b for
 * exactly this reason.
 *
 * The split is not half-and-half. Each part is a thing this wizard can WATCH
 * happen, so a truncated paste is caught by the step that owns it:
 *
 *   Part 1 · tunnel  → a WireGuard handshake appears. Step 2 waits for it.
 *   Part 2 · access  → SmartFi's REST probe answers. Step 3 waits for that.
 *
 * ─── NOBODY IS EVER TRAPPED ON A STEP ───
 * Next is never disabled by a detection that has not fired yet. A tunnel can
 * take a minute, and a wizard that refuses to move because a poll is late is a
 * wizard the operator closes. The step advances ITSELF when the router reports
 * in; the button is there for when it does not.
 *
 * NO POWER-CYCLE — unlike the vendo's step 4a. That step exists because the
 * vendo script uses hotspot, fetch and scheduler, all blocked by RouterOS's
 * restricted device-mode. Neither of these parts is restricted.
 */
interface Props {
   onCancel: () => void;
   onAdded: () => void;
   /** Reopening an existing server's script rather than registering a new one. */
   showScriptFor?: { id: string; name: string } | null;
}

/**
 * Registration, and nothing else.
 *
 * Ray, 2026-09-02: *"if we put setup script here it doesnt make sense because
 * setup script is for registration purpose only right"* — right. Parts 1 and 2
 * MUST be pasted because until they land SmartFi has no way into the router.
 * Everything after that SmartFi can write itself over the tunnel, so the
 * walled garden is a button on the Servers page, not two more pastes here.
 */
const STEPS = [
   { num: 1, label: 'Name it' },
   { num: 2, label: 'Paste part 1' },
   { num: 3, label: 'Paste part 2' },
   { num: 4, label: 'Done' },
] as const;

type StepNum = 1 | 2 | 3 | 4;

const IspServerAdd: React.FC<Props> = ({ onCancel, onAdded, showScriptFor }) => {
   const [name, setName] = useState('');
   const [model, setModel] = useState('hex-rb750gr3');
   // Where subscribers arrive. '' = every LAN port (what all routers got
   // before 2026-10-29). See the Step 1 fields below.
   const [subPort, setSubPort] = useState('');
   const [vlan, setVlan] = useState('');
   const [busy, setBusy] = useState(false);
   const [error, setError] = useState('');
   /** The long version, shown only when the two-line fetch will not run. */
   const [showFull, setShowFull] = useState(false);
   const [parts, setParts] = useState<IspScriptPart[]>([]);
   const [serverId, setServerId] = useState<string | null>(
      showScriptFor?.id ?? null
   );
   const [serverName, setServerName] = useState(showScriptFor?.name ?? '');
   // Reopening an existing server lands on the paste steps, not on a name form
   // it has already answered.
   const [step, setStep] = useState<StepNum>(showScriptFor ? 2 : 1);


   const { data: models } = useQuery({
      queryKey: ['isp-server-models'],
      queryFn: getIspServerModels,
      staleTime: 3600_000,
   });

   // Steps 2 and 3 answer themselves. The operator is standing at the router;
   // asking them whether the paste worked would be asking the wrong party — and
   // a truncated paste is precisely the case where they would say yes.
   const { data: servers } = useQuery({
      queryKey: ['isp-servers'],
      queryFn: getIspServers,
      enabled: !!serverId,
      refetchInterval: 5000,
   });
   const me = servers?.find((s) => s.id === serverId);

   const tunnelUp = !!me?.online;
   const readable = me?.setupStage === 'ready';
   /** Part 1 landed and part 2 did not — the shape a truncated paste leaves. */
   const halfDone = me?.setupStage === 'tunnel-only';

   /**
    * How long step 2 has been waiting, so a stall can say something useful.
    *
    * 🔴 FOUR DIFFERENT FAULTS LOOK IDENTICAL HERE. A tunnel that never comes up
    * means the paste was truncated, OR the router has no internet, OR UDP to the
    * endpoint is blocked, OR it is dialling with a key from a previous
    * registration. From SmartFi's side they are the same silence — we cannot
    * reach a router whose tunnel is down to ask which. So after a minute the
    * wizard stops saying "usually under a minute" and names all four, because a
    * list the operator can walk beats a spinner that knows nothing.
    */
   const [waitedSec, setWaitedSec] = useState(0);
   useEffect(() => {
      // Runs on BOTH paste steps: step 2 waits for the tunnel, step 3 waits for
      // the probe, and each needs to know how long it has been waiting before it
      // starts suggesting something is wrong.
      const waitingOnThisStep =
         (step === 2 && !tunnelUp) || (step === 3 && !readable);
      if (!waitingOnThisStep) return;
      const t = setInterval(() => setWaitedSec((v) => v + 5), 5000);
      return () => clearInterval(t);
   }, [step, tunnelUp, readable]);
   // Reset when they move on, so coming BACK to step 2 does not open on a
   // stalled message about a wait that already succeeded.
   // Reset on arriving at a step, so step 3 does not inherit step 2's clock and
   // accuse somebody the moment they get there.
   useEffect(() => {
      setWaitedSec(0);
   }, [step]);

   useEffect(() => {
      if (!showScriptFor) return;
      getIspServerScript(showScriptFor.id)
         .then(setParts)
         .catch(() => setError('Could not load the script. Close and try again.'));
   }, [showScriptFor]);



   // Advance on the router's own report rather than on a click. Only ever
   // FORWARD from the step that was waiting, so an operator who stepped back to
   // re-read part 1 is not yanked away mid-sentence.
   useEffect(() => {
      if (step === 2 && tunnelUp) setStep(3);
   }, [step, tunnelUp]);
   useEffect(() => {
      if (step === 3 && readable) setStep(4);
   }, [step, readable]);



   const register = async () => {
      if (name.trim().length < 2) {
         setError('Give this router a name.');
         return;
      }
      const vlanNum = vlan.trim() === '' ? 0 : Number(vlan.trim());
      if (
         !Number.isInteger(vlanNum) ||
         vlanNum === 1 ||
         vlanNum < 0 ||
         vlanNum > 4094
      ) {
         setError('VLAN number must be from 2 to 4094 — or leave it empty.');
         return;
      }
      setError('');
      setBusy(true);
      try {
         const r = await createIspServer({
            name: name.trim(),
            model,
            subscriberPort: sfpPort && subPort === sfpPort ? subPort : '',
            subscriberVlan: vlanNum,
         });
         setParts(r.parts);
         setServerId(r.id);
         setServerName(r.name);
         setStep(2);
         onAdded();
      } catch (e: unknown) {
         setError(
            (e as { response?: { data?: { error?: string } } })?.response?.data
               ?.error ?? 'Could not add it. Try again.'
         );
      } finally {
         setBusy(false);
      }
   };

   const copy = async (part: IspScriptPart) => {
      try {
         await navigator.clipboard.writeText(part.script);
         toast.success(`${part.label} copied.`);
      } catch {
         toast.error('Could not copy — select the script and copy by hand.');
      }
   };

   /** This model's SFP, if it has one — the only other place we offer. */
   const sfpPort = (models ?? []).find((m) => m.id === model)?.sfpPort ?? '';

   const tunnelPart = parts.find((p) => p.id === 'tunnel');
   const accessPart = parts.find((p) => p.id === 'access');
   /** The generator refused rather than hand over a script with a hole in it. */
   const refused = parts.length === 1 && parts[0].label === 'Cannot generate';

   const doneUpTo = (n: number) =>
      (n === 1 && !!serverId) ||
      (n === 2 && tunnelUp) ||
      (n === 3 && readable);

   /* ─── Step bodies ──────────────────────────────────────── */

   const renderStep1 = () => (
      <StepPane>
         <div>
            <StepEyebrow $active>Step 1 · Name it</StepEyebrow>
            <br />
            <StepTitle>Which MikroTik is this?</StepTitle>
         </div>
         <Grid>
            <SettingsField>
               <SettingsLbl>Name</SettingsLbl>
               <SettingsInput
                  value={name}
                  placeholder="e.g. Brgy. Lagao"
                  onChange={(e) => setName(e.target.value)}
               />
            </SettingsField>
            <SettingsField>
               <SettingsLbl>Model</SettingsLbl>
               <Sel value={model} onChange={(e) => setModel(e.target.value)}>
                  {(models ?? []).map((m) => (
                     <option key={m.id} value={m.id}>
                        {m.label}
                     </option>
                  ))}
               </Sel>
               <SettingsHintSm>Port names differ per board.</SettingsHintSm>
            </SettingsField>
            {/* 🔴 ASKED HERE, NOT FIXED BY HAND LATER. DARAYO (2026-10-29)
                built VLAN 30 himself with "Use Service Tag" ticked and every
                customer was silently dropped. SmartFi builds it plain. */}
            <SettingsField>
               <SettingsLbl>Subscribers plug into</SettingsLbl>
               <Sel
                  value={sfpPort && subPort === sfpPort ? subPort : ''}
                  onChange={(e) => setSubPort(e.target.value)}
               >
                  <option value="">LAN ports (ether2 and up)</option>
                  {sfpPort && (
                     <option value={sfpPort}>SFP port ({sfpPort})</option>
                  )}
               </Sel>
               <SettingsHintSm>
                  Where your OLT or switch connects to this MikroTik.
               </SettingsHintSm>
            </SettingsField>
            <SettingsField>
               <SettingsLbl>VLAN number</SettingsLbl>
               <SettingsInput
                  type="text"
                  inputMode="numeric"
                  value={vlan}
                  placeholder="Leave empty if not on a VLAN"
                  onChange={(e) => setVlan(e.target.value.replace(/\D/g, ''))}
               />
               <SettingsHintSm>
                  The VLAN your OLT sends subscribers on, e.g. 30. Used when
                  SmartFi sets up a freshly reset MikroTik.
               </SettingsHintSm>
            </SettingsField>
         </Grid>
         {/* Stated here because it is the one thing that must be true BEFORE
             either paste, and there is no Quick Set to make it true. A vendo's
             script sets the WAN up because that MikroTik comes out of the box;
             this one is already the operator's working PPPoE server, and
             touching its internet would cut the subscribers on it. */}
         <StepNote>
            <strong>Before you paste:</strong> this MikroTik must already have
            internet — the tunnel is built over it. There is no Quick Set here on
            purpose: this router is already serving your subscribers, so SmartFi
            leaves its WAN exactly as you have it, along with your PPPoE server,
            pools, profiles and secrets.
         </StepNote>
         {error && <Err>{error}</Err>}
      </StepPane>
   );

   const renderPaste = (
      part: IspScriptPart | undefined,
      n: 2 | 3,
      done: boolean
   ) => (
      <StepPane>
         <div>
            <StepEyebrow $active={!done}>
               Step {n} · Paste part {n - 1}
            </StepEyebrow>
            <br />
            <StepTitle>
               {n === 2
                  ? done
                     ? 'Tunnel is up — part 1 arrived whole'
                     : 'Paste part 1 in Winbox → New Terminal'
                  : done
                    ? 'SmartFi can read this router'
                    : 'Paste part 2 in the same terminal'}
            </StepTitle>
         </div>

         {refused && <Err>{parts[0].script}</Err>}

         {part && (
            <>
               {/* 🔴 STEPS 2 AND 3 MUST NOT LOOK THE SAME. Ray, 2026-08-31: "in
                   step 1 and step it looks similar, i feel i didnt move because
                   the UI is alittle bit the same." Two screens with a dark code
                   block and a blue Copy button ARE the same picture, and an
                   operator who cannot tell they advanced will paste the wrong
                   half. So each part carries its own colour, its own big number
                   and its own one-line job. */}
               <PartBanner $n={n}>
                  <PartBadge $n={n}>{n - 1}</PartBadge>
                  <PartText>
                     <PartTitle>
                        Part {n - 1} of 2 — {n === 2 ? 'the tunnel' : 'the access'}
                     </PartTitle>
                     <PartWhat>{part.what}</PartWhat>
                  </PartText>
               </PartBanner>
               <ScriptRow>
                  <ScriptLabel>{part.then}</ScriptLabel>
                  <CopyBtn type="button" onClick={() => copy(part)}>
                     Copy part {n - 1}
                  </CopyBtn>
               </ScriptRow>
               <ScriptPre>{part.script}</ScriptPre>

               {/* 🔴 SOMEWHERE TO GO WHEN THE FETCH CANNOT RUN. Ray,
                   2026-09-03, on why part 2 is now two lines: *"i notice the
                   step 1 and step 2 script here is long."* Fetching removes
                   the paste-length wall and the silent truncation with it —
                   but a router behind a proxy or a blocked DNS cannot fetch at
                   all, and an operator staring at a fetch error needs an
                   answer that is not "call support". */}
               {part.fallback ? (
                  <>
                     <FallbackLink
                        type="button"
                        onClick={() => setShowFull((v) => !v)}
                     >
                        {showFull
                           ? 'Hide the long version'
                           : 'The fetch did not work — paste it in full instead'}
                     </FallbackLink>
                     {showFull ? (
                        <>
                           <FallbackNote>
                              Paste this ONE line at a time if Winbox cuts it
                              short. That is the reason the fetch exists.
                           </FallbackNote>
                           <ScriptPre>{part.fallback}</ScriptPre>
                        </>
                     ) : null}
                  </>
               ) : null}

               {/* THE state this split exists to surface. A router with a live
                   tunnel and no REST is what a truncated part 2 looks like, and
                   it would otherwise read as a healthy online router that
                   happens to report nothing. */}
               {/* ⚠️ NOT THE MOMENT THEY ARRIVE. `tunnel-only` is true the
                   instant the tunnel comes up — which is exactly when step 3
                   opens — so this used to accuse the operator of a missing paste
                   before they had been given the chance to make it. Ray saw it
                   on a clean run with part 2 still in his clipboard. It waits
                   until they have plausibly pasted and it has plausibly failed. */}
               {/* And never once the router has FETCHED part 2: then the
                   stage is only waiting for SmartFi's next look, and the error
                   on screen is from before the paste (Ray, 2026-10-30). */}
               {n === 3 && halfDone && !me?.part2Fetched && waitedSec >= 45 && (
                  <HalfWarn>
                     <strong>Part 2 has not arrived.</strong> The tunnel is up, so
                     part 1 landed — but SmartFi still cannot read this router.{' '}
                     {me?.health?.lastProbeError
                        ? me.health.lastProbeError
                        : 'Winbox drops the end of a long paste without saying so.'}{' '}
                     Copy part 2 again and check the router prints{' '}
                     <Mono>SmartFi part 2 of 2 done</Mono>.
                  </HalfWarn>
               )}

               {/* Only once it has genuinely been a while. Showing this
                   immediately would teach an operator to distrust a paste that
                   is merely 20 seconds old. */}
               {n === 2 && !done && waitedSec >= 60 && (
                  <StallHint>
                     <strong>Still nothing after {Math.floor(waitedSec / 60)} minute
                     {waitedSec >= 120 ? 's' : ''}.</strong> From here the tunnel
                     being down looks the same whatever the cause, so it is worth
                     checking all four:
                     <ol>
                        <li>
                           Did the paste arrive whole? The router should have
                           printed <Mono>SmartFi part 1 of 2 done</Mono>. If a
                           line is missing from the terminal, copy part 1 again.
                        </li>
                        <li>
                           <strong>Does this MikroTik have internet?</strong>{' '}
                           SmartFi does not set its WAN up — that would wipe the
                           PPPoE server on a live one. Check it can reach the
                           internet on its own first.
                        </li>
                        <li>
                           Is outbound UDP to the tunnel endpoint allowed? Some
                           office and hotel networks block it.
                        </li>
                        <li>
                           Was this router registered before? Part 1 replaces an
                           old <Mono>wg-smartfi</Mono>, so re-pasting it clears a
                           key SmartFi no longer recognises.
                        </li>
                     </ol>
                  </StallHint>
               )}

               {/* 🔴 A LIVE, OBVIOUS WAIT. Ray: "theres no friendly indication
                   for tunnel handshake… it feels its lag or whats next. or do i
                   ahve to click the next button." A one-line grey sentence beside
                   a static dot reads as a page that has finished rendering, not
                   one that is working — so the operator sits wondering whether
                   the ball is in their court. It never is: this step completes on
                   the router's own report and advances itself. */}
               {done ? (
                  <DoneBanner>
                     <DoneTick>✓</DoneTick>
                     <span>
                        {n === 2
                           ? `Tunnel is up${
                                me?.handshakeAgoSec !== null &&
                                me?.handshakeAgoSec !== undefined
                                   ? ` — last seen ${me.handshakeAgoSec}s ago`
                                   : ''
                             }. Moving you on.`
                           : 'SmartFi is reading this router — clients, CPU and memory are filling in.'}
                     </span>
                  </DoneBanner>
               ) : n === 3 && !tunnelUp ? (
                  <WaitBanner>
                     <span>
                        Paste part 1 first — part 2 needs the tunnel it builds.
                     </span>
                  </WaitBanner>
               ) : (
                  <WaitBanner>
                     <Spinner aria-hidden />
                     <WaitText>
                        <strong>
                           {n === 2
                              ? 'Waiting for the router to dial home…'
                              : 'Waiting for SmartFi to read the router…'}
                        </strong>
                        <WaitSub>
                           This usually takes 20 to 60 seconds and is worth the
                           wait. Nothing to click — this step finishes itself and
                           moves you on.
                           {waitedSec >= 15 && ` (${waitedSec}s)`}
                        </WaitSub>
                     </WaitText>
                  </WaitBanner>
               )}
            </>
         )}
      </StepPane>
   );

   const renderDone = () => (
      <StepPane>
         <div>
            <StepEyebrow $active>Step 4 · Done</StepEyebrow>
            <br />
            <StepTitle>
               {readable
                  ? `${serverName} is connected`
                  : `${serverName} is registered`}
            </StepTitle>
         </div>
         {readable ? (
            <>
               <StepNote>
                  SmartFi is reading this router now. Its clients, CPU, memory and
                  PPPoE services appear on the Servers list, and refresh on their
                  own while you have the page open.
               </StepNote>
               <SummaryRows>
                  <SummaryRow>
                     <SummaryKey>Clients online</SummaryKey>
                     <SummaryVal>
                        {me?.health?.onlineClients ?? '—'}
                        {me?.health?.routerSecretCount !== null &&
                        me?.health?.routerSecretCount !== undefined
                           ? ` of ${me.health.routerSecretCount} on the router`
                           : ''}
                     </SummaryVal>
                  </SummaryRow>
                  <SummaryRow>
                     <SummaryKey>RouterOS</SummaryKey>
                     <SummaryVal>
                        {me?.routerOsVersion || '—'}
                        {me?.health?.boardName ? ` · ${me.health.boardName}` : ''}
                     </SummaryVal>
                  </SummaryRow>
                  <SummaryRow>
                     <SummaryKey>PPPoE service</SummaryKey>
                     <SummaryVal>
                        {me?.servers?.length
                           ? me.servers.map((p) => p.name).join(', ')
                           : 'none found'}
                     </SummaryVal>
                  </SummaryRow>
               </SummaryRows>
            </>
         ) : (
            <StepNote>
               It is saved either way — it will show under{' '}
               <strong>Waiting for setup</strong> on the Servers list until both
               pastes have landed, and you can pick the scripts back up from there.
            </StepNote>
         )}
      </StepPane>
   );

   const canNext = step === 1 ? !serverId || !busy : true;

   const next = () => {
      if (step === 1) {
         if (!serverId) return register();
         return setStep(2);
      }
      setStep(Math.min(4, step + 1) as StepNum);
   };

   return (
      <Wrapper>
         <Stepper>
            {STEPS.map((s, i) => (
               <React.Fragment key={s.num}>
                  <Bullet
                     $active={s.num === step}
                     $done={s.num < step || doneUpTo(s.num)}
                  >
                     {s.num < step || doneUpTo(s.num) ? '✓' : s.num}
                  </Bullet>
                  {i < STEPS.length - 1 && (
                     <Connector $done={s.num < step || doneUpTo(s.num)} />
                  )}
               </React.Fragment>
            ))}
         </Stepper>
         <StepCaption>
            Step {step} of {STEPS.length} —{' '}
            <strong>{STEPS[step - 1].label}</strong>
         </StepCaption>

         {step === 1 && renderStep1()}
         {step === 2 && renderPaste(tunnelPart, 2, tunnelUp)}
         {step === 3 && renderPaste(accessPart, 3, readable)}
         {step === 4 && renderDone()}

         <NavRow>
            <Ghost type="button" onClick={onCancel}>
               {step === 4 ? 'Close' : 'Cancel'}
            </Ghost>
            <NavBtns>
               {step > 1 && (
                  <Ghost type="button" onClick={() => setStep((step - 1) as StepNum)}>
                     Back
                  </Ghost>
               )}
               {step < 4 && (
                  <Primary type="button" disabled={!canNext} onClick={next}>
                     {step === 1 && !serverId
                        ? busy
                           ? 'Registering…'
                           : 'Register & continue'
                        : 'Next'}
                  </Primary>
               )}
               {step === 4 && (
                  <Primary type="button" onClick={onCancel}>
                     Done
                  </Primary>
               )}
            </NavBtns>
         </NavRow>
      </Wrapper>
   );
};

export default IspServerAdd;

const Wrapper = styled.div`
   width: 660px;
   max-width: 100%;
`;

const Grid = styled.div`
   display: grid;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   gap: 12px 16px;

   @media (max-width: 720px) {
      grid-template-columns: minmax(0, 1fr);
   }
`;

const Sel = styled.select`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   &:focus {
      border-color: ${colors.PRIMARY};
   }
`;

const Err = styled.p`
   margin: 0;
   font-size: 12px;
   color: ${colors.DANGER};
   white-space: pre-wrap;
`;

/**
 * The half-pasted router.
 *
 * Amber rather than red: nothing is broken, one paste is missing. Red here would
 * send an operator hunting for a fault in a router that is working exactly as it
 * was left.
 */
const HalfWarn = styled.p`
   margin: 0;
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid ${colors.ORANGE}55;
   background: ${colors.ORANGE}10;
   font-size: 12px;
   line-height: 1.6;
   color: ${colors.MAIN_DARK};
`;

const Mono = styled.code`
   font-family: 'SF Mono', ui-monospace, Menlo, monospace;
   font-size: 11px;
   padding: 1px 5px;
   border-radius: 4px;
   background: ${colors.GRAY14};
`;

const SummaryRows = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
   padding: 12px 14px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   background: ${colors.GRAY14};
`;

const SummaryRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;
   gap: 12px;
   font-size: 12px;
`;

const SummaryKey = styled.span`
   color: ${colors.GRAY5};
`;

const SummaryVal = styled.span`
   font-weight: 600;
   color: ${colors.MAIN_DARK};
   text-align: right;
`;

const Primary = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: none;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;

   &:disabled {
      opacity: 0.6;
      cursor: default;
   }
`;

const Ghost = styled.button`
   padding: 8px 16px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY12};
   background: transparent;
   color: inherit;
   font-size: 13px;
   cursor: pointer;
`;

/**
 * What to check when the tunnel has not come up.
 *
 * Amber, not red: nothing is broken yet, and on a slow link a minute is not
 * unusual. It is a checklist, not an error.
 */
const StallHint = styled.div`
   padding: 12px 14px;
   border-radius: 8px;
   border: 1px solid ${colors.ORANGE}55;
   background: ${colors.ORANGE}10;
   font-size: 12px;
   line-height: 1.65;
   color: ${colors.MAIN_DARK};
   ol {
      margin: 8px 0 0;
      padding-left: 20px;
   }
   li + li {
      margin-top: 6px;
   }
`;

/* ─── Telling the two paste steps apart ───────────────────── */

/**
 * Each part gets its OWN colour, number and one-line job.
 *
 * Every paste screen is otherwise the same picture — a dark code block and a
 * Copy button — which is exactly why Ray could not tell he had moved on:
 * *"in step 1 and step it looks similar, i feel i didnt move."* With four
 * parts rather than two, telling them apart matters more, not less: pasting
 * part 3 where part 4 belongs leaves a wall with no way through it.
 */
const PART_TONE: Record<2 | 3, string> = {
   2: colors.PRIMARY,
   3: colors.BLUE,
};
const PartBanner = styled.div<{ $n: 2 | 3 }>`
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 10px 12px;
   border-radius: 10px;
   border: 1px solid
      ${({ $n }) => ($n === 2 ? `${colors.PRIMARY}40` : `${colors.BLUE}40`)};
   background: ${({ $n }) =>
      $n === 2 ? `${colors.PRIMARY}0d` : `${colors.BLUE}0d`};
`;

const PartBadge = styled.div<{ $n: 2 | 3 }>`
   width: 30px;
   height: 30px;
   flex: 0 0 auto;
   border-radius: 50%;
   display: grid;
   place-items: center;
   font-size: 14px;
   font-weight: 800;
   color: ${colors.WHITE};
   background: ${({ $n }) => PART_TONE[$n]};
`;

const PartText = styled.div`
   min-width: 0;
`;

const PartTitle = styled.div`
   font-size: 13px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;

const PartWhat = styled.div`
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.GRAY5};
`;

/* ─── Waiting, visibly ────────────────────────────────────── */

const WaitBanner = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 12px 14px;
   border-radius: 10px;
   border: 1px solid ${colors.GRAY12};
   background: ${colors.GRAY14};
`;

const WaitText = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   font-size: 12.5px;
   color: ${colors.MAIN_DARK};
`;

const WaitSub = styled.span`
   font-size: 11.5px;
   line-height: 1.55;
   color: ${colors.GRAY5};
`;

/** Movement, so the screen reads as working rather than finished. */
const Spinner = styled.span`
   width: 18px;
   height: 18px;
   flex: 0 0 auto;
   border-radius: 50%;
   border: 2px solid ${colors.GRAY12};
   border-top-color: ${colors.PRIMARY};
   animation: isp-spin 0.8s linear infinite;
   @keyframes isp-spin {
      to {
         transform: rotate(360deg);
      }
   }
   @media (prefers-reduced-motion: reduce) {
      animation-duration: 2.4s;
   }
`;

const DoneBanner = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 12px 14px;
   border-radius: 10px;
   border: 1px solid ${colors.SUCCESS}55;
   background: ${colors.SUCCESS}10;
   font-size: 12.5px;
   color: ${colors.MAIN_DARK};
`;

const DoneTick = styled.span`
   width: 20px;
   height: 20px;
   flex: 0 0 auto;
   border-radius: 50%;
   display: grid;
   place-items: center;
   font-size: 12px;
   font-weight: 800;
   color: ${colors.WHITE};
   background: ${colors.SUCCESS};
`;
